import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { BillingPageSummary } from "./models/BillingPageSummary";
import { MemberBalance } from "./models/MemberBalance";
import { MonthEndReview } from "./models/MonthEndReview";
import { MonthlyStatement } from "./models/MonthlyStatement";
import {
  fetchBillingSummary,
  fetchMemberBalance,
  fetchMonthEndReview,
  fetchMonthlyStatement,
} from "./slice-extensions/billing-slice-extension";

interface BillingState {
  summary?: BillingPageSummary;
  summaryStatus: LoadingStatus;
  balancesByMemberId: Record<string, MemberBalance>;
  balanceStatus: LoadingStatus;
  monthEndReview?: MonthEndReview;
  monthEndStatus: LoadingStatus;
  monthlyStatement?: MonthlyStatement;
  monthlyStatementStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: BillingState = {
  summary: undefined,
  summaryStatus: LoadingStatus.Idle,
  balancesByMemberId: {},
  balanceStatus: LoadingStatus.Idle,
  monthEndReview: undefined,
  monthEndStatus: LoadingStatus.Idle,
  monthlyStatement: undefined,
  monthlyStatementStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    clearBillingError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingSummary.pending, (state) => {
        state.summaryStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchBillingSummary.fulfilled, (state, action: PayloadAction<BillingPageSummary>) => {
        state.summaryStatus = LoadingStatus.Idle;
        state.summary = action.payload;
      })
      .addCase(fetchBillingSummary.rejected, (state, action) => {
        state.summaryStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(fetchMemberBalance.pending, (state) => {
        state.balanceStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchMemberBalance.fulfilled, (state, action: PayloadAction<MemberBalance>) => {
        state.balanceStatus = LoadingStatus.Idle;
        state.balancesByMemberId[action.payload.memberId] = action.payload;
      })
      .addCase(fetchMonthEndReview.pending, (state) => {
        state.monthEndStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchMonthEndReview.fulfilled, (state, action: PayloadAction<MonthEndReview>) => {
        state.monthEndStatus = LoadingStatus.Idle;
        state.monthEndReview = action.payload;
      })
      .addCase(fetchMonthEndReview.rejected, (state, action) => {
        state.monthEndStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(fetchMonthlyStatement.pending, (state) => {
        state.monthlyStatementStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchMonthlyStatement.fulfilled, (state, action: PayloadAction<MonthlyStatement>) => {
        state.monthlyStatementStatus = LoadingStatus.Idle;
        state.monthlyStatement = action.payload;
      })
      .addCase(fetchMonthlyStatement.rejected, (state, action) => {
        state.monthlyStatementStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(fetchMemberBalance.rejected, (state, action) => {
        state.balanceStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const { clearBillingError } = billingSlice.actions;

export const selectBillingSummary = (state: RootState) => state.billing.summary;
export const selectBillingSummaryStatus = (state: RootState) => state.billing.summaryStatus;
export const selectMemberBalance = (memberId: string) => (state: RootState) =>
  state.billing.balancesByMemberId[memberId];
export const selectBillingError = (state: RootState) => state.billing.lastError;

export default billingSlice.reducer;

export const selectMonthEndReview = (state: RootState) => state.billing.monthEndReview;
export const selectMonthEndStatus = (state: RootState) => state.billing.monthEndStatus;
export const selectMonthlyStatement = (state: RootState) => state.billing.monthlyStatement;
export const selectMonthlyStatementStatus = (state: RootState) =>
  state.billing.monthlyStatementStatus;
